/**
 * KAN-950: floating video tutorial button for cabinet pages.
 * Renders nothing when the current page has no video (and on /m/*).
 * Position comes from the registry entry (placement).
 * The modal (TutorialDialog) plays the video of the page and lists the
 * other videos of its section.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, PlayCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TutorialPlacement } from '@/tutorials/registry';
import { useCurrentTutorial } from './useCurrentTutorial';
import { useTutorialCollapsed } from './useTutorialCollapsed';
import { TutorialDialog } from './TutorialDialog';
import { OnboardingButton } from './OnboardingButton';

/** Bottom-right sits above the page bottom; top-right sits under CabinetTopBar (h-12). */
const PLACEMENT_CLASSES: Record<TutorialPlacement, string> = {
  'bottom-right': 'bottom-6 right-6',
  'top-right': 'top-14 right-6',
};

export function TutorialButton() {
  const tutorial = useCurrentTutorial();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useTutorialCollapsed();
  /** Bumped on every opening: the modal remounts and starts with the page video. */
  const [session, setSession] = useState(0);

  if (!tutorial) return null;

  const openModal = () => {
    setSession((value) => value + 1);
    setOpen(true);
  };

  const label = `Видео-подсказка: ${tutorial.title}`;
  const compact = tutorial.placement === 'top-right';
  const sizeClasses = compact
    ? collapsed
      ? 'h-10 w-10'
      : 'h-10 px-4 text-sm'
    : collapsed
      ? 'h-14 w-14'
      : 'h-14 px-6 text-base';

  return (
    <>
      <div
        data-testid="tutorial-button-root"
        data-collapsed={String(collapsed)}
        data-placement={tutorial.placement}
        className={cn(
          'fixed z-40 flex items-center gap-1 print:hidden',
          PLACEMENT_CLASSES[tutorial.placement]
        )}
      >
        {/* KAN-952: on top-right pages (chat) "С чего начать" sits in this row, left of the button. */}
        {compact && <OnboardingButton inline />}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Развернуть видео-подсказку' : 'Свернуть видео-подсказку'}
          title={collapsed ? 'Развернуть' : 'Свернуть'}
          className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-gray-500 shadow ring-1 ring-gray-200 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {collapsed ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        <span className="relative inline-flex">
          {/* Attention ring: a few pulses after load, then stays transparent. */}
          <span
            aria-hidden="true"
            data-testid="tutorial-button-pulse"
            className="pointer-events-none absolute inset-0 rounded-full bg-blue-500 opacity-60 motion-safe:animate-ping"
            style={{ animationIterationCount: 3, animationFillMode: 'forwards' }}
          />
          <button
            type="button"
            onClick={openModal}
            data-tutorial-open=""
            aria-label={label}
            title={label}
            className={cn(
              'relative inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 font-semibold text-white shadow-lg shadow-blue-600/40 ring-4 ring-blue-200 transition hover:scale-105 hover:bg-blue-700 focus:outline-none focus-visible:ring-blue-400',
              sizeClasses
            )}
          >
            <PlayCircle
              className={compact ? 'h-6 w-6' : collapsed ? 'h-8 w-8' : 'h-7 w-7'}
              aria-hidden="true"
            />
            {!collapsed && <span>Видео-подсказка</span>}
          </button>
        </span>
      </div>

      <TutorialDialog
        key={`${tutorial.slug}:${session}`}
        open={open}
        onOpenChange={setOpen}
        start={{ slug: tutorial.slug, title: tutorial.title, videoUrl: tutorial.videoUrl }}
      />
    </>
  );
}
